/* eslint-disable react/no-unknown-property */
import { observer } from 'mobx-react-lite';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { emitOnce, NodeColumns, tableStore } from '../../stores/table-store';
import { runInAction, toJS } from 'mobx';

interface TableCellProps {
  rowId: string;
  // index?: number;
  type?: React.HTMLInputTypeAttribute | undefined;
  min?: string;
  max?: string;
  minWidth?: number | string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columnId?: string | number | symbol | any;

  onChangeConversion?: (input: string) => unknown;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  renderValue?: (arg: any) => string;
}

export const EditableTableCell: React.FC<TableCellProps> = observer(
  ({
    rowId,
    min,
    max,
    columnId,
    type = 'text',
    minWidth = `calc(100% + 24px)`,
    onChangeConversion,
    renderValue,
    ...props
  }) => {
    const latestValueFromMobx = tableStore.getValue(rowId, columnId);
    const settings = tableStore.getValue(rowId, NodeColumns.settings);
    const cellId = `${rowId}-${columnId}`;

    const getCellValue = useCallback(() => {
      return tableStore.getValue(rowId, columnId);
    }, [rowId, columnId]);

    const initialValue = getCellValue();
    const [inputValue, setInputValue] = useState<string>(initialValue);
    const [originalValueOnFocus, setOriginalValueOnFocus] =
      useState<string>(initialValue);
    const index = tableStore.getRowIndex(rowId);
    const { pendingEditCellId } = tableStore;

    const inputRef = useRef<HTMLInputElement>(null);
    // PSTUDIO-2154: committed value captured at focus to register a single
    // undo/redo command on blur.
    const beforeEditSnapshotRef = useRef<Record<string, unknown>>({});

    useLayoutEffect(() => {
      if (pendingEditCellId === cellId && inputRef.current) {
        tableStore.pendingEditCellId = null;
        const input = inputRef.current;
        input.focus();

        requestAnimationFrame(() => {
          if (input.type !== 'number') {
            const len = (input.value ?? '').length;
            input.setSelectionRange(len, len);
          }
        });
      }
    }, [pendingEditCellId, cellId]);

    const handleFocus = () => {
      runInAction(() => {
        emitOnce(() => {
          if (
            tableStore.editingCellId !== cellId &&
            !tableStore.pendingEditCellId
          ) {
            tableStore.pendingEditCellId = cellId;
          }
          tableStore.isCellBeingEdited = true;
          tableStore.editingCellId = cellId;
          tableStore.editingRowId = rowId;
          tableStore.editingColumnId = columnId;
        });
      });

      // Capture the value when user focuses to detect real changes
      setOriginalValueOnFocus(inputValue);
      // Snapshot the committed store value at focus so an undo/redo command can
      // restore it exactly on blur (PSTUDIO-2154).
      beforeEditSnapshotRef.current = {
        [columnId]: toJS(tableStore.getValue(rowId, columnId)),
      };
    };

    const handleBlur = () => {
      setTimeout(() => {
        tableStore.isCellBeingEdited = false;
        if (tableStore.editingCellId === cellId) {
          tableStore.editingCellId = null;
          tableStore.editingRowId = null;
          tableStore.editingColumnId = null;
        }
      }, 50);

      const currentMobxValue = tableStore.getValue(rowId, columnId);

      // Only save if value actually changed from when user focused
      // This prevents saving when user types then reverts back
      if (currentMobxValue !== originalValueOnFocus) {
        tableStore.saveUnsavedChanges();
        // In manual validation mode, clear the error for this cell when data was changed
        tableStore.clearCellValidationError(
          rowId,
          columnId as keyof typeof NodeColumns
        );
        // Register the committed edit as a single undo/redo command
        // (PSTUDIO-2154).
        tableStore.commitCellEdit({
          rowId,
          columnId,
          before: beforeEditSnapshotRef.current,
        });
      } else {
        // Value reverted to original - just clear dirty flag without saving
        tableStore.clearRowDirty(rowId);
      }

      // Handle task rows
      if (settings?.taskId && tableStore.isTaskRow(settings?.taskId)) {
        const currentValue = tableStore.getValue(rowId, columnId);
        // Convert empty string to null for number inputs to avoid DB errors
        const processedValue =
          type === 'number' && currentValue === '' ? null : currentValue;

        tableStore.updateSolutionForTaskRow(settings?.taskId, {
          [columnId]: processedValue,
        });
      }
    };

    useEffect(() => {
      if (inputValue === undefined) return;

      // if (tableStore.selectedCells.has(cellId)) return;

      if (inputValue !== latestValueFromMobx) {
        setInputValue(latestValueFromMobx);
      }
    }, [inputValue, latestValueFromMobx, cellId]);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const valueToRender = useCallback(
      (test: string) => {
        if (test === null || test === '') return test;

        if (renderValue) {
          return renderValue(test) as string;
        } else if (columnId === 'parameterInput') {
          if (typeof test === 'object') {
            return JSON.stringify(test);
          } else {
            return test;
          }
        } else {
          return test || '';
        }
      },
      [renderValue, columnId]
    );

    if (tableStore.isEditing()) {
      return (
        <input
          ref={inputRef}
          test-id={`c-editor-table--${columnId}-${index}-input`}
          type={type}
          min={min}
          max={max}
          value={valueToRender(inputValue || '')}
          onChange={(e) => {
            const { value } = e.target;

            setInputValue(value);

            // Update MobX store immediately (but don't save to DB)
            if (onChangeConversion) {
              const result = onChangeConversion(value);

              if (result) {
                tableStore.updateRow({
                  rowId,
                  updates: {
                    [columnId]: result,
                  },
                  skipValidationRules: true,
                  saveImmediately: false,
                });
              }
            } else {
              // Convert empty string to null for number inputs to avoid DB errors
              const processedValue =
                type === 'number' && value === '' ? null : value;

              tableStore.updateRow({
                rowId,
                updates: {
                  [columnId]: processedValue,
                },
                skipValidationRules: true,
                saveImmediately: false,
              });
            }

            // Track this row as having unsaved changes
            tableStore.markRowDirty(rowId);
          }}
          onFocus={handleFocus}
          onBlur={handleBlur}
          {...props}
          style={{
            minWidth: typeof minWidth === 'number' ? `${minWidth}px` : minWidth,
            pointerEvents:
              tableStore.editingCellId === cellId ? 'auto' : 'none',
          }}
        />
      );
    }

    const truncateInViewMode =
      columnId === 'output' ||
      columnId === 'whatsNext' ||
      columnId === 'variables';

    return (
      <div
        className={
          truncateInViewMode ? 'c-editor-table-cell__view-value' : undefined
        }
      >
        {valueToRender(inputValue || '')}
      </div>
    );
  }
);
